import { isNonRepeatableItem, ScheduleItem } from "@lib/types";
import { UiFloatModal } from "../../../common/modal";
import { ScheduleForm, ScheduleFormSubmitEvent } from "../../components/scheduleForm/scheduleForm";
import { mapFormValuesToScheduleItem, mapScheduleItemToFormValues } from "../../components/scheduleForm/scheduleForm.helper";
import { mapFromRRuleFrequency, ScheduleRecurrence } from "../../types";
import { showConfirmationPopup } from "../confirmationPopup/confirmationPopup";

// Side effect import to define custom element.
// eslint-disable-next-line no-duplicate-imports
import "../../components/scheduleForm/scheduleForm";

type EditScheduleModalParams = Readonly<{
    scheduleItem: ScheduleItem;
    isSingle: boolean;
    parentScheduleItem?: ScheduleItem;
    initialDateStart?: Date;
}>;

/** Edit schedule modal. */
export class EditScheduleModal {

    /** Popup instance. */
    public readonly popup: UiFloatModal;

    private readonly form: ScheduleForm;

    private initialScheduleItem: ScheduleItem | null = null;

    private parentScheduleItem: ScheduleItem | null = null;

    private initialDate: Date | null;

    private isSingle = false;

    public constructor(
        onSave: (scheduleItem: ScheduleItem, isSingle: boolean, initialItem: ScheduleItem | null, initialDate: Date | null) => void,
        onDelete: (scheduleItem: ScheduleItem, isSingle: boolean, date: Date) => void,
    ) {
        try {
            this.popup = new UiFloatModal(
                "<schedule-form class=\"form full-height\" isEdit=\"true\"></schedule-form>",
                true,
                { bottom: true, right: true, left: true },
                { width: 578, height: 300 },
            );
            this.popup.modalComponent.x = 10;
            this.popup.modalComponent.y = 10;
            this.popup.modalComponent.minWidth = 578;

            const form = this.popup.modalComponent.querySelector<ScheduleForm>(".form");

            if (form === null) {
                throw new Error("Could not find schedule form element.");
            }

            this.form = form;

            this.popup.modalComponent.onClose = () => {
                this.form.closeChildModals();
                this.resetState();
            };

            this.form.addEventListener("scheduleFormSubmit", (event: ScheduleFormSubmitEvent) => {
                const scheduleItem = mapFormValuesToScheduleItem(
                    event.detail.values,
                    this.initialScheduleItem?.id ?? 0,
                    this.initialScheduleItem?.parentId ?? null,
                    this.isSingle,
                );

                const handleSave = (): void => {
                    onSave(scheduleItem, this.isSingle, this.parentScheduleItem, this.initialDate);
                    this.form.closeChildModals();
                    this.close();
                };

                if (!this.isSingle && !isNonRepeatableItem(scheduleItem)) {
                    const { x, y } = event.detail.submitter.getBoundingClientRect();
                    showConfirmationPopup({
                        position: {
                            x,
                            y,
                        },
                        title: "",
                        description: `Если Вы изменили определенные события в ряду,
                            изменения будут отменены, отдельные события будут снова соответствовать ряду`,
                        onConfirm() {
                            handleSave();
                        },
                    });
                } else {
                    handleSave();
                }
            });
            this.form.addEventListener("scheduleFormCancel", () => {
                this.form.closeChildModals();
                this.handleFormCancel();
            });
            this.form.addEventListener("scheduleFormDelete", () => {
                if (this.initialDate !== null && this.initialScheduleItem !== null) {
                    onDelete(this.initialScheduleItem, this.isSingle, this.initialDate);
                }
                this.form.closeChildModals();
                this.close();
            });

        } catch(e) {
            console.error("Error <initPopup> >> ", e);
        }
    }

    /**
     * Opens modal.
     * @param params Open parameters.
     */
    public open(params: EditScheduleModalParams): void {
        this.initialScheduleItem = params.scheduleItem;
        this.isSingle = params.isSingle;
        this.parentScheduleItem = params.parentScheduleItem ?? null;
        this.initialDate = params.initialDateStart ?? null;
        const initialFormValues = mapScheduleItemToFormValues(
            params.scheduleItem,
            params.isSingle ?
                params.parentScheduleItem?.rrule.clone() :
                undefined,
        );
        this.form.isRecurrenceEditDisabled = params.isSingle;
        this.form.initialValues = initialFormValues;
        this.form.isSingle = params.isSingle;

        const recurrence = mapFromRRuleFrequency(params.scheduleItem.rrule.rrules()[0].options.freq);

        if (recurrence === ScheduleRecurrence.NoRepeat && params.scheduleItem.parentId === null) {
            this.popup.modalComponent.label = "Редактировать событие";
        } else if (params.isSingle) {
            this.popup.modalComponent.label = "Редактировать событие ряда";
        } else {
            this.popup.modalComponent.label = "Редактировать ряд событий";
        }

        this.popup.uiModal.resetSize();
        this.popup.modalComponent.open();
    }

    /** Resets modal state. */
    public resetState(): void {
        this.initialScheduleItem = null;
        this.parentScheduleItem = null;
        this.initialDate = null;
        this.isSingle = false;
    }

    /** Schedule ID. */
    public get scheduleId(): number | null {
        return this.initialScheduleItem?.id ?? null;
    }

    /** Parent schedule ID. */
    public get parentScheduleId(): number | null {
        return this.parentScheduleItem?.id ?? null;
    }

    /** Closes modal. */
    public close(): void {
        this.popup.modalComponent.close();
    }

    private handleFormCancel(): void {
        this.close();
    }
}
